<template>
  <v-card class="facility-check-in-modal">
    <v-card-title>
      {{ $t('admin_acc_check_in.title') }}
    </v-card-title>
    <v-card-text>
      <div class="facility-check-in-wrapper">
        <div class="facility-check-in-bookings">
          <div class="d-flex align-center justify-center">
            <h5>{{ $t('admin_acc_check_in.bookings') }}</h5>
            <v-btn class="ml-4"
                   small
                   @click="scanForBookings">
              <v-icon>mdi-plus</v-icon>
              <v-icon>mdi-qrcode</v-icon>
            </v-btn>
          </div>
          <div class="d-flex flex-column flex-grow-1 overflow-auto">
            <AdminBookingList full-height
                              show-select
                              hide-select-all
                              hide-search
                              hide-actions
                              :items-per-page="99"
                              :extra-filters="currentBookingFilters"
                              v-model="selectedBookingIds"></AdminBookingList>
          </div>
        </div>
        <v-divider vertical></v-divider>
        <div class="facility-check-in-white-cards">
          <div class="d-flex align-center justify-center">
            <h5>{{ $t('admin_acc_check_in.white_cards') }}</h5>
            <v-btn class="ml-4"
                   small
                   @click="scanForWhiteCards">
              <v-icon>mdi-plus</v-icon>
              <v-icon>mdi-qrcode</v-icon>
            </v-btn>
          </div>
          <div class="d-flex flex-column flex-grow-1 overflow-auto">
            <WhiteCardList full-height
                           show-select
                           hide-select-all
                           hide-search
                           hide-default-footer
                           hide-actions
                           :items-per-page="99"
                           :extra-filters="currentWhiteCardFilters"
                           v-model="selectedWhiteCardIds"></WhiteCardList>
          </div>
        </div>
      </div>
    </v-card-text>
    <v-card-actions class="text-right">
      <v-btn text
             color="primary"
             @click="scanUserQrCode">
        {{ $t('admin_acc_booking.check_in_scan_user_title') }}
      </v-btn>

      <EditMemberModal :shown="editMemberModalShown"
                       :account-id="editingMemberId"
                       @cancel="onEditMemberModalFinish"
                       @finish="onEditMemberModalFinish"></EditMemberModal>
      <v-spacer></v-spacer>
      <v-btn text
             @click="$emit('submit', undefined)">
        {{ $t('cancel') }}
      </v-btn>
      <v-btn text
             color="primary"
             :disabled="selectedBookingIds.length === 0 || selectedWhiteCardIds.length === 0"
             @click="checkIn">
        {{ $t('ok') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {computed, onMounted, ref, watch} from "@vue/composition-api";
import JwtScanner from "@/components/acc_booking/JwtScanner";
import AdminBookingList from "@/components/acc_booking/booking/AdminBookingList";
import WhiteCardList from "@/components/acc_booking/white_card/WhiteCardList";
import {DateTime} from "luxon";
import EditMemberModal from "@/components/member/EditMemberModal.vue";

export default {
  name: 'AdminFacilityCheckInModal',
  components: {EditMemberModal, WhiteCardList, AdminBookingList},
  setup(props, {root, emit}) {
    const currentBookingFilters = computed(() => {
      return {
        'id': relatedBookingIds.value,
      }
    });
    const relatedBookingIds = ref([]);
    const selectedBookingIds = ref([]);
    const scanForBookings = async function () {
      let keepGoing = true;
      while (keepGoing) {
        const {bki, sub} = await root.$dialog.showAndWait(JwtScanner, {
          title: root.$t('admin_acc_booking.check_in_scan_title'),
          targetKey: ['bki', 'sub'],
        });

        if (bki) {
          let set = new Set(relatedBookingIds.value);
          set.add(bki);
          relatedBookingIds.value = [...set];

          let s = new Set(selectedBookingIds.value);
          s.add(bki);
          selectedBookingIds.value = [...s];
        } else if (sub) {
          // Get All Bookings of User today
          await root.$callAPI('aafb/admin/bookings/', {
            user_id: sub,
            start_time: DateTime.now().startOf('day').toISO(),
            end_time: DateTime.now().endOf('day').toISO(),
          }, (response) => {
            let set = new Set(relatedBookingIds.value);
            let s = new Set(selectedBookingIds.value);
            response.body.bookings.forEach((b) => {
              set.add(b.id);
              s.add(b.id);
            })
            relatedBookingIds.value = [...set];
            selectedBookingIds.value = [...s];
          })

          keepGoing = false;
        } else {
          keepGoing = false;
        }
      }
    }

    const currentWhiteCardFilters = computed(() => {
      return {
        'id': relatedWhiteCardIds.value,
      }
    });
    const relatedWhiteCardIds = ref([]);
    const selectedWhiteCardIds = ref([]);
    watch(() => selectedWhiteCardIds.value, (newValue) => {
      relatedWhiteCardIds.value = [...newValue];
    })
    const scanForWhiteCards = async function () {
      let keepGoing = true;
      while (keepGoing) {
        let cardId = await root.$dialog.showAndWait(JwtScanner, {
          title: root.$t('admin_acc_gym_checkin.scan_white_card'),
          targetKey: 'wht',
          manualInputKey: 'wht',
          manualInputLabel: root.$t('admin_acc_white_card.id'),
        });

        if (cardId === undefined) return;

        //
        let regex = /^A([0-9]*)$/;
        if (regex.test(cardId)) {
          cardId = parseInt(cardId.match(regex)[1])
        } else {
          cardId = parseInt(cardId)
        }

        if (cardId) {
          let set = new Set(relatedWhiteCardIds.value);
          set.add(cardId);
          relatedWhiteCardIds.value = [...set];

          let s = new Set(selectedWhiteCardIds.value);
          s.add(cardId);
          selectedWhiteCardIds.value = [...s];
        } else {
          alert('Failed to Scan White Card')
        }
      }
    }

    const editingMemberId = ref(null);
    const editMemberModalShown = ref(false);
    const onEditMemberModalFinish = function () {
      editingMemberId.value = null;
      editMemberModalShown.value = false;
    }
    const scanUserQrCode = async function () {
      const {sub} = await root.$dialog.showAndWait(JwtScanner, {
        title: root.$t('admin_acc_booking.check_in_scan_user_title'),
        targetKey: ['sub'],
      });

      if (sub) {
        editingMemberId.value = sub;
        editMemberModalShown.value = true;
      }
    }

    const checkIn = async function () {
      if (await root.$dialog.confirm({
        text: root.$t('admin_acc_check_in.confirm_msg', {booking_count: selectedBookingIds.value.length, white_card_count: selectedWhiteCardIds.value.length}),
        showClose: false,
        persistent: true,
      })) {
        root.$callAPI('aafb/admin/booking/checkin/', {
          booking_ids: selectedBookingIds.value,
          white_card_ids: selectedWhiteCardIds.value,
        }, () => {
          root.$showAlertMessage(root.$t('admin_acc_booking.checkin_success_msg'));
          emit('submit', true);
        })
      }
    }

    onMounted(() => {
      // scanForBookings();
    })

    return {
      currentBookingFilters,
      relatedBookingIds,
      selectedBookingIds,
      scanForBookings,

      currentWhiteCardFilters,
      relatedWhiteCardIds,
      selectedWhiteCardIds,
      scanForWhiteCards,

      editingMemberId,
      editMemberModalShown,
      onEditMemberModalFinish,
      scanUserQrCode,
      checkIn,

    }
  }
}
</script>

<style lang="less">
.facility-check-in-modal {
  display: flex !important;
  flex-direction: column !important;
  height: 90vh;
  overflow: hidden;

  .v-card__text {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    width: 100% !important;
  }

  .facility-check-in-wrapper {
    flex: 1 1 0;
    display: flex;

    .facility-check-in-bookings {
      flex: 2 2 0;
      overflow: auto;

      display: flex;
      flex-direction: column;
    }

    .facility-check-in-white-cards {
      flex: 1 1 0;
      overflow: auto;

      display: flex;
      flex-direction: column;
    }
  }
}
</style>
